import { getRuleSeverityFromResources, RULE_SEVERITIES, RuleEntity } from '@internal/backstage-plugin-catalog-devops-common';

export { scanLabel } from '@internal/backstage-plugin-catalog-devops-common';

export const ruleTitle = (rule: RuleEntity): string =>
  rule.metadata.title ?? rule.metadata.name;

/** Worst severity across the rule's affected resources. */
export const severityOf = (rule: RuleEntity): string =>
  getRuleSeverityFromResources(
    rule.spec.resources as Array<{ severity?: string }>,
  ) ?? 'Unknown';

export const severityRank = (severity: string): number => {
  const index = (RULE_SEVERITIES as readonly string[]).indexOf(severity);
  return index === -1 ? RULE_SEVERITIES.length : index;
};

export const affectedCount = (rule: RuleEntity): number =>
  rule.spec.resources?.length ?? 0;

/** Max saving of a rule, or null when the export has no usable numbers. */
export const maxSavingOf = (rule: RuleEntity): number | null => {
  const max = Number(rule.spec.maxCostSaving);
  return Number.isFinite(max) && max > 0 ? max : null;
};

export const savingText = (rule: RuleEntity): string => {
  const max = maxSavingOf(rule);
  return max === null ? '' : `$${max.toLocaleString('en-US')}/mo`;
};

/** Names of the RuleSets a rule belongs to (relations.partOf, ruleset kind
 * only — the account relation is partOf too). */
export const rulesetsOf = (rule: RuleEntity): string[] => {
  const names = (rule.relations ?? [])
    .filter(
      relation =>
        relation.type === 'partOf' &&
        relation.targetRef.startsWith('ruleset:'),
    )
    .map(relation => relation.targetRef.split('/').pop() ?? '');
  return [...new Set(names)].filter(Boolean);
};

export const accountOf = (entity: { relations?: { type: string; targetRef: string }[] }): string =>
  entity.relations
    ?.filter(relation => relation.type === 'partOf')
    .map(relation => relation.targetRef)
    .find(ref => ref.startsWith('account:'))
    ?.split('/')
    .pop() ?? '';

/** Relative "2 d ago" label for scan/report timestamps (from common). */
export const pillClass = (classes: Record<string, string>, value: string): string => {
  const normalized = value.toLowerCase();
  const known = [
    'critical',
    'high',
    'medium',
    'low',
    'info',
    'unknown',
    'failed',
    'passed',
  ];
  return known.includes(normalized)
    ? classes[`pill${normalized[0].toUpperCase()}${normalized.slice(1)}`]
    : classes.pillUnknown;
};

/**
 * Severity a rule carries for ONE resource: spec.resources[] is per-resource
 * (a rule can be High on one resource and Low on another). Falls back to the
 * rule's worst severity when the resource is not listed.
 */
export const resourceSeverityOf = (
  rule: RuleEntity,
  resourceName: string,
): string => {
  const entry = (rule.spec.resources ?? []).find(
    resource => resource.id === resourceName,
  );
  return entry?.severity ?? severityOf(rule);
};

/** Per-resource remediation note (spec.resources[].note). */
export const resourceNoteOf = (
  rule: RuleEntity,
  resourceName: string,
): string | undefined =>
  (rule.spec.resources ?? []).find(resource => resource.id === resourceName)
    ?.note;

/** Sibling tab path of the current entity page (/catalog/.../<name> + '/rules'). */
export const siblingTabPath = (pathname: string, tab: string): string =>
  `${pathname.replace(/\/+$/, '')}/${tab}`;

/** Finding status (Active/Resolved/Archived) → pill grammar value. */
export const findingPillValue = (status: string): string => {
  const tone = (status ?? '').toLowerCase();
  if (tone === 'active') {
    return 'failed';
  }
  if (tone === 'resolved') {
    return 'passed';
  }
  return 'unknown';
};
